import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  GitBranch, 
  Search,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  company_id: '',
  branch_name: '',
  branch_address: '',
  active: true
};

export const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [branchesRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/branches`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders())
      ]);
      setBranches(branchesRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingBranch(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (branch) => {
    setFormData({
      company_id: branch.company_id || '',
      branch_name: branch.branch_name || '',
      branch_address: branch.branch_address || '',
      active: branch.active ?? true
    });
    setEditingBranch(branch);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.company_id) {
      setError('Please select a company');
      return;
    }
    if (!formData.branch_name.trim()) {
      setError('Branch name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingBranch) {
        await axios.put(
          `${BACKEND_URL}/api/branches/${editingBranch.id}`,
          formData,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/branches`,
          formData,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save branch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (branchId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/branches/${branchId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete branch');
    }
  };

  const filteredBranches = branches.filter(b =>
    b.branch_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.branch_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="branches-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Branches</h1>
          <p className="text-slate-600 mt-1">Manage your branch locations</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-branch-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Branch
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search branches..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Branches Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredBranches.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <GitBranch className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No branches yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first branch</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Branch
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-5 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Branch Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Address</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredBranches.map((branch) => (
              <div
                key={branch.id}
                data-testid={`branch-row-${branch.id}`}
                className="grid grid-cols-1 md:grid-cols-5 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div>
                  <div className="font-medium text-slate-900">{branch.branch_name}</div>
                </div>
                <div className="text-slate-600 text-sm">{branch.company_name || '-'}</div>
                <div className="text-slate-600 text-sm truncate">{branch.branch_address || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    branch.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {branch.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {branch.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(branch)}
                    data-testid={`edit-btn-${branch.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(branch.id)}
                    data-testid={`delete-btn-${branch.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this branch? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Company Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Company <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="company_id"
                    value={formData.company_id}
                    onChange={handleInputChange}
                    data-testid="input-company"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  >
                    <option value="">Select a company</option>
                    {companies.filter(c => c.active).map(company => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Branch Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Branch Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleInputChange}
                    data-testid="input-branch-name"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Branch Address */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Branch Address</label>
                  <textarea
                    name="branch_address"
                    value={formData.branch_address}
                    onChange={handleInputChange}
                    rows={3}
                    data-testid="input-branch-address"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    data-testid="input-active"
                    className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-slate-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingBranch ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
